import React, { memo, useReducer } from "react";
import { IControl, MapInstance, useControl } from "react-map-gl/mapbox";
import { createPortal } from "react-dom";

class SvgOverlayControl implements IControl {
  private _map: MapInstance | null = null;
  private _container?: HTMLDivElement;
  private _reset: () => void;

  constructor(reset: () => void) {
    this._reset = reset;
  }

  onAdd(map: MapInstance): HTMLElement {
    this._map = map;
    map.on("move", this._reset);
    this._container = document.createElement("div");
    this._reset();
    return this._container;
  }

  onRemove(_map: MapInstance): void {
    this._container?.remove();
    this._map?.off("move", this._reset);
    this._map = null;
  }

  getDefaultPosition() {
    return "top-left" as const;
  }

  getMap() {
    return this._map;
  }

  getElement() {
    return this._container;
  }
}

// children is a render function so every map move renders a fresh element;
// returning the same element would let React skip re-projecting the overlay.
const SvgOverlay = (props: { children: () => React.ReactElement }) => {
  const [, forceUpdate] = useReducer((version: number) => version + 1, 0);
  const ctrl = useControl<SvgOverlayControl>(
    () => new SvgOverlayControl(forceUpdate),
  );

  const map = ctrl.getMap();
  const container = ctrl.getElement();

  return map && container ? createPortal(props.children(), container) : null;
};

export default memo(SvgOverlay);
